// 인증 추상화 계층 — poc-portal api/auth.ts 이식판 (Vite env → CRA env로만 변경)
import axios from 'axios';
import {
  GW_ORIGIN,
  REFRESH_KEY,
  TOKEN_KEY,
  authorizedFetch,
  clearTokens,
  configureAuth,
  decodeClaims,
  getFreshToken,
  isExpired,
  isRefreshExpired,
  onSessionExpired,
  refreshTokens,
  saveTokens,
  startRefreshExpiryWatch,
} from 'yukimushi-component-fe';
import type { TokenClaims } from 'yukimushi-component-fe';
import type { User } from '../types';
import { getLang } from '../lib/locale';
import { formatPersonName } from '../lib/personName';

/**
 * 인증서버(Keycloak)는 내부망에 있고, 프론트는 게이트웨이의 /api/auth/* 프록시만 호출한다.
 * 리다이렉트 없음 — 자체 로그인 폼에서 아이디/비밀번호로 처리(ROPC).
 *
 * 게이트웨이 계약:
 *   POST /api/auth/login   {username, password} → {access_token, refresh_token, expires_in(600), ...}
 *   POST /api/auth/refresh {refreshToken}       → 새 토큰 쌍
 *   POST /api/auth/logout  {refreshToken}       → 204
 * 사용자 정보는 별도 /me 없이 JWT 클레임(preferred_username·name·deptCode)에서 취한다.
 *
 * 토큰 저장·만료 검사·재발급·세션 만료 통지는 공용 유틸(yukimushi-component-fe의 auth/session)이
 * 갖고 있다 — 모듈도 같은 구현을 쓰기 때문이다. 이 파일은 호스트만의 책임인 로그인/로그아웃/세션복구다.
 */

export interface Credentials {
  username: string;
  password: string;
}

/** 패스워드 저장 설정 키 (체크 시 자격증명을 저장해 로그인 화면에 자동 입력) */
export const AUTO_LOGIN_KEY = 'poc_portal_auto_login';

/* 기존 호출부(aiChat·AuthContext)가 이 모듈에서 가져다 쓰는 이름은 그대로 재export 한다 */
export { TOKEN_KEY, REFRESH_KEY, refreshTokens, getFreshToken, authorizedFetch, onSessionExpired };

export interface AuthResult {
  token: string;
  user: User;
}

export interface AuthService {
  /** ID/PW 기반 자체 로그인 폼 방식 */
  loginWithCredentials(creds: Credentials): Promise<AuthResult>;
  /** 로그아웃 (서버 세션 종료 시도 포함) */
  logout(): Promise<void>;
  /** 저장된 토큰으로 세션 복구 — refresh가 없거나 만료면 access와 무관하게 실패, access가 없거나 만료면 재발급 */
  restoreSession(token: string | null): Promise<AuthResult>;
}

/** Keycloak 표준 클레임 family_name/given_name — 공용 TokenClaims 타입에는 아직 없어 여기서 넓혀 읽는다 */
type NameClaims = TokenClaims & { family_name?: string; given_name?: string };

function toUser(claims: NameClaims): User {
  const familyName = claims.family_name?.trim() || undefined;
  const givenName = claims.given_name?.trim() || undefined;
  const fullName = claims.name?.trim();
  return {
    // 성·이름이 둘 다 있으면 표기 규칙대로 합치고, 없으면 name 클레임을 그대로 쓴다
    name: formatPersonName({ familyName, givenName, fullName }, getLang()) || claims.preferred_username || '사용자',
    familyName,
    givenName,
    username: claims.preferred_username ?? '',
    position: claims.deptCode ?? '',
    avatar: null,
  };
}

/* ────────────────────────────────────────────────────────────────
 * Mock 구현: 인증 없이 UI/플로우만 확인할 때 (REACT_APP_AUTH_MODE=mock)
 * ──────────────────────────────────────────────────────────────── */
const MOCK_USER: User = { name: '박명국', familyName: '박', givenName: '명국', username: 'mkpark', position: '책임연구원', avatar: null };

function delay<T>(value: T, ms = 400): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

class MockAuthService implements AuthService {
  async loginWithCredentials(creds: Credentials): Promise<AuthResult> {
    if (!creds.username || !creds.password) {
      throw new Error('아이디와 비밀번호를 입력해 주세요.');
    }
    return delay({ token: `mock-token-${Date.now()}`, user: { ...MOCK_USER, name: creds.username } });
  }

  async logout(): Promise<void> {
    return delay(undefined, 150);
  }

  async restoreSession(token: string | null): Promise<AuthResult> {
    if (!token) throw new Error('로그인이 필요합니다');
    return delay({ token, user: MOCK_USER }, 150);
  }
}

/* ────────────────────────────────────────────────────────────────
 * 게이트웨이 인증 프록시 구현
 * ──────────────────────────────────────────────────────────────── */
// 어느 origin에서 서빙되든 게이트웨이는 dev(dev.gennolab.co.jp)뿐이다 — 판정은 공용 유틸의 GW_ORIGIN
const AUTH_BASE = process.env.REACT_APP_AUTH_BASE_URL ?? `${GW_ORIGIN}/chat-api/api/auth`;

// 공용 유틸의 /refresh 호출도 같은 base를 쓰게 맞춘다 (env 재정의를 모듈까지 전파하는 유일한 지점)
configureAuth({ authBase: AUTH_BASE });

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

const http = axios.create({ baseURL: AUTH_BASE });

class GatewayAuthService implements AuthService {
  async loginWithCredentials(creds: Credentials): Promise<AuthResult> {
    try {
      const { data } = await http.post<TokenResponse>('/login', creds);
      // 프록시·리다이렉트 등으로 토큰 없는 2xx가 오는 경우 방어
      if (!data?.access_token) {
        throw new Error('아이디 또는 비밀번호가 올바르지 않습니다.');
      }
      saveTokens(data.access_token, data.refresh_token);
      startRefreshExpiryWatch(); // refresh 만료 시각에 access를 폐기하고 로그인으로 돌리는 타이머
      return { token: data.access_token, user: toUser(decodeClaims(data.access_token)) };
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        if (status === 400 || status === 401 || status === 403) {
          throw new Error('아이디 또는 비밀번호가 올바르지 않습니다.');
        }
        throw new Error('로그인 처리 중 오류가 발생했습니다.');
      }
      throw err;
    }
  }

  async logout(): Promise<void> {
    const refreshToken = localStorage.getItem(REFRESH_KEY);
    clearTokens();
    if (refreshToken) {
      // 서버 세션 종료는 최선 노력 — 실패해도 로컬 로그아웃은 진행
      await http.post('/logout', { refreshToken }).catch(() => undefined);
    }
  }

  async restoreSession(saved: string | null): Promise<AuthResult> {
    // refresh가 없거나 만료면 access가 살아 있어도 세션은 끝 — access를 즉시 폐기하고 재로그인으로 보낸다.
    // 여기서는 이벤트 없이 조용히 실패한다(첫 렌더 시점이라 AuthContext의 만료 구독이 아직 없다).
    if (isRefreshExpired()) {
      clearTokens();
      throw new Error('로그인이 만료되었습니다. 다시 로그인하세요');
    }
    // access가 없거나 만료면 refresh로 재발급 — refresh만 살아 있어도 로그인 화면으로 보내지 않는다
    let token: string | null = saved;
    if (!token || isExpired(token)) {
      token = await refreshTokens(); // access 수명 10분 — 새로고침 복구는 대부분 이 경로
      if (!token) throw new Error('로그인이 만료되었습니다. 다시 로그인하세요');
    }
    startRefreshExpiryWatch();
    return { token, user: toUser(decodeClaims(token)) };
  }
}

/* ────────────────────────────────────────────────────────────────
 * 활성 구현 선택 — 기본은 게이트웨이 연동, REACT_APP_AUTH_MODE=mock 시 목업
 * ──────────────────────────────────────────────────────────────── */
const authService: AuthService =
  process.env.REACT_APP_AUTH_MODE === 'mock' ? new MockAuthService() : new GatewayAuthService();

export default authService;
